import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api, apiError } from '../lib/api';

// Enhanced styling for the search page
const searchPageStyle = `
  @keyframes searchGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(67, 56, 202, 0.15), 0 10px 30px -10px rgba(67, 56, 202, 0.2); }
    50% { box-shadow: 0 0 0 4px rgba(67, 56, 202, 0.08), 0 14px 40px -10px rgba(67, 56, 202, 0.35); }
  }
  @keyframes searchBorderShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes shimmerSweep {
    0% { transform: translateX(-120%) skewX(-20deg); }
    100% { transform: translateX(220%) skewX(-20deg); }
  }
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  /* Beautiful stacked search box */
  .srch-stack {
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    margin-bottom: 1.5rem;
    position: relative;
    overflow: visible;
  }
  .srch-input-wrap {
    position: relative;
    width: 100%;
    z-index: 10000;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(120deg, #C7D2FE, #A78BFA, #818CF8, #C7D2FE);
    background-size: 300% 300%;
    animation: searchBorderShift 6s ease infinite, searchGlow 3s ease-in-out infinite;
    transition: transform 0.25s ease;
  }
  .srch-input-wrap:focus-within {
    transform: translateY(-1px);
    animation: searchBorderShift 3s ease infinite, searchGlow 1.8s ease-in-out infinite;
  }
  .srch-input-inner {
    position: relative;
    border-radius: 10px;
    background: #ffffff;
    overflow: hidden;
    display: flex;
    align-items: center;
  }
  .srch-input-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #818CF8;
    pointer-events: none;
    transition: color 0.25s ease;
  }
  .srch-input-wrap:focus-within .srch-input-icon { color: #4338CA; }
  .srch-input-field {
    width: 100%;
    border: none !important;
    outline: none !important;
    background: transparent !important;
    padding: 0.78rem 1rem 0.78rem 2.7rem !important;
    font-size: 0.98rem;
    color: #1a202c;
    box-shadow: none !important;
  }
  .srch-input-field::placeholder { color: #a0aec0; font-weight: 400; }
  .srch-input-inner::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(238, 242, 255, 0.6), transparent);
    transform: translateX(-120%) skewX(-20deg);
    pointer-events: none;
  }
  .srch-input-wrap:focus-within .srch-input-inner::before {
    animation: shimmerSweep 1.8s ease-in-out infinite;
  }
  .srch-clear-btn {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: none;
    background: #EEF2FF;
    color: #6366F1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .srch-clear-btn:hover {
    background: #4338CA;
    color: #fff;
    transform: translateY(-50%) scale(1.08);
  }

  .srch-bottom {
    display: flex;
    gap: 0.7rem;
    align-items: center;
    flex-wrap: wrap;
  }
  .srch-bottom .city-select,
  .srch-bottom .cat-select {
    flex: 1 1 200px;
    min-width: 170px;
  }
  .srch-bottom .search-submit { flex: 0 0 auto; }

  .animated-city-item:hover {
    background: linear-gradient(90deg, #EEF2FF, #F5F3FF, #EEF2FF) !important;
  }

  .animated-search-btn {
    background: linear-gradient(120deg, #4338CA, #6366F1, #818CF8, #4338CA);
    background-size: 300% 300%;
    animation: gradientShift 4s ease infinite;
    transition: all 0.3s ease;
    color: white;
    border: none;
    height: 46px;
    padding: 0 1.8rem;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
  }
  .animated-search-btn:hover {
    animation: gradientShift 2s ease infinite;
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(67, 56, 202, 0.4);
  }

  .animated-dropdown-trigger {
    background: linear-gradient(120deg, #ffffff, #F5F3FF, #EEF2FF, #ffffff);
    background-size: 300% 300%;
    animation: gradientShift 6s ease infinite;
    transition: all 0.3s ease;
  }
  .animated-dropdown-trigger:hover {
    animation: gradientShift 3s ease infinite;
    border-color: #4338CA !important;
    box-shadow: 0 4px 20px rgba(67, 56, 202, 0.15) !important;
  }

  @media (max-width: 768px) {
    .srch-bottom { flex-direction: column; align-items: stretch; }
    .srch-bottom .city-select,
    .srch-bottom .cat-select,
    .srch-bottom .search-submit { flex: 1 1 auto; min-width: 0; width: 100%; }
  }
`;

export default function Search() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [q, setQ] = useState(params.get('q') || '');
  const [city, setCity] = useState(params.get('city') || '');
  const [category, setCategory] = useState(params.get('category') || '');
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Business name suggestions
  const [businessSuggestions, setBusinessSuggestions] = useState([]);
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const businessDropdownRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [c, ci] = await Promise.all([api.get('/categories'), api.get('/cities')]);
      setCategories(c.data.categories || []);
      setCities(ci.data.cities || []);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true); setErr('');
      try {
        const qs = new URLSearchParams();
        if (params.get('q')) qs.set('q', params.get('q'));
        if (params.get('city')) qs.set('city', params.get('city'));
        if (params.get('category')) qs.set('category', params.get('category'));
        if (params.get('subcategory')) qs.set('subcategory', params.get('subcategory'));
        if (params.get('premium')) qs.set('premium', params.get('premium'));
        const { data } = await api.get('/businesses?' + qs.toString());
        setBusinesses(data.businesses || []);
      } catch (e) { setErr(apiError(e, 'Failed to load')); }
      finally { setLoading(false); }
    })();
  }, [params]);

  // Keep inputs in sync with URL params when they change externally (e.g. browser back)
  useEffect(() => {
    setQ(params.get('q') || '');
    setCity(params.get('city') || '');
    setCategory(params.get('category') || '');
  }, [params]);

  // Debounced fetch for business name suggestions
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const term = q.trim();
    if (term.length < 2) {
      setBusinessSuggestions([]);
      setLoadingSuggestions(false);
      return;
    }

    setLoadingSuggestions(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/businesses', {
          params: { q: term, limit: 8 }
        });
        setBusinessSuggestions(res.data.businesses || []);
      } catch (e) {
        console.error(apiError(e));
        setBusinessSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [q]);

  // Close suggestion dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (businessDropdownRef.current && !businessDropdownRef.current.contains(event.target)) {
        setShowBusinessDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    setShowBusinessDropdown(false);
    const p = {};
    if (q) p.q = q; if (city) p.city = city; if (category) p.category = category;
    setParams(p);
  };

  const clearFilter = (k) => {
    const p = Object.fromEntries(params); delete p[k]; setParams(p);
    if (k === 'q') setQ(''); if (k === 'city') setCity(''); if (k === 'category') setCategory('');
  };

  const selectBusiness = (biz) => {
    setShowBusinessDropdown(false);
    nav(`/business/${biz.slug}`);
  };

  const goToFullSearch = (term) => {
    setShowBusinessDropdown(false);
    const p = {};
    if (term) p.q = term;
    if (city) p.city = city;
    if (category) p.category = category;
    setParams(p);
  };

  return (
    <div className="container my-4" data-testid="search-page">
      <style>{searchPageStyle}</style>

      <h1 className="mb-3">Search results</h1>

      <form className="mb-4" onSubmit={doSearch} data-testid="search-form">
        <div className="srch-stack">
          {/* TOP: Business name search with suggestions */}
          <div ref={businessDropdownRef} className="srch-input-wrap">
            <div className="srch-input-inner">
              <span className="srch-input-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </span>
              <input
                className="form-control srch-input-field"
                placeholder="Search business name, e.g. Pizza Hut"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setShowBusinessDropdown(true);
                }}
                onFocus={() => {
                  if (q.trim().length >= 2) setShowBusinessDropdown(true);
                }}
                data-testid="search-input"
              />
              {q && (
                <button
                  type="button"
                  className="srch-clear-btn"
                  onClick={() => {
                    setQ('');
                    setBusinessSuggestions([]);
                    setShowBusinessDropdown(false);
                  }}
                  aria-label="Clear search"
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 2L8 8M8 2L2 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>

            {showBusinessDropdown && q.trim().length >= 2 && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  boxShadow: '0 24px 60px -12px rgba(67, 56, 202, 0.25)',
                  zIndex: 99999,
                  maxHeight: 340,
                  overflowY: 'auto',
                  padding: '0.35rem'
                }}
              >
                {loadingSuggestions && (
                  <div style={{ padding: '0.75rem 1rem', color: '#a0aec0', fontSize: '0.9rem' }}>
                    Searching…
                  </div>
                )}

                {!loadingSuggestions && businessSuggestions.length === 0 && (
                  <div style={{ padding: '0.75rem 1rem', color: '#a0aec0', fontSize: '0.9rem' }}>
                    No businesses found for "{q}"
                  </div>
                )}

                {businessSuggestions.map((b) => (
                  <div
                    key={b.id}
                    className="animated-city-item"
                    onClick={() => selectBusiness(b)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.65rem',
                      padding: '0.55rem 0.75rem',
                      cursor: 'pointer',
                      borderRadius: 8
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: '#EEF2FF',
                        backgroundImage: (b.logo_url || b.cover_url) ? `url(${b.logo_url || b.cover_url})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#818CF8',
                        fontWeight: 800,
                        flexShrink: 0
                      }}
                    >
                      {!(b.logo_url || b.cover_url) && b.name.substring(0, 1).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#1a202c', fontSize: '0.92rem' }}>
                        {b.name}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                        {b.category_name}
                        {b.city_name ? ` · ${b.city_name}` : ''}
                      </div>
                    </div>
                  </div>
                ))}

                {!loadingSuggestions && businessSuggestions.length > 0 && (
                  <div
                    onClick={() => goToFullSearch(q)}
                    style={{
                      marginTop: '0.35rem',
                      padding: '0.6rem 0.9rem',
                      borderTop: '1px solid #eef2ff',
                      cursor: 'pointer',
                      color: '#4338CA',
                      fontWeight: 600,
                      fontSize: '0.88rem',
                      borderRadius: 8,
                      background: '#fafbff'
                    }}
                  >
                    See all results for "{q}" →
                  </div>
                )}
              </div>
            )}
          </div>

          {/* BOTTOM: City + Category + Apply */}
          <div className="srch-bottom">
            <select
              className="form-select city-select"
              value={city}
              onChange={e=>setCity(e.target.value)}
              data-testid="search-city-select"
              style={{ height: '46px', borderRadius: '10px' }}
            >
              <option value="">Any city</option>
              {cities.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
            </select>

            <select
              className="form-select cat-select"
              value={category}
              onChange={e=>setCategory(e.target.value)}
              data-testid="search-category-select"
              style={{ height: '46px', borderRadius: '10px' }}
            >
              <option value="">Any category</option>
              {categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
            </select>

            <button
              className="animated-search-btn search-submit"
              type="submit"
              data-testid="search-submit"
            >
              Apply
            </button>
          </div>
        </div>
      </form>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {Array.from(params.entries()).map(([k,v]) => (
          <span key={k+v} className="badge rounded-pill text-bg-light border" style={{padding:'.5rem .8rem'}} data-testid={`filter-${k}`}>
            {k}: {v} <button className="btn btn-sm p-0 ms-2" onClick={() => clearFilter(k)} data-testid={`clear-${k}`}>×</button>
          </span>
        ))}
      </div>

      {loading ? <div className="text-center py-5"><span className="ap-loader" /></div> : (
        err ? <div className="alert alert-danger">{err}</div> :
        businesses.length === 0 ? <div className="ap-card">No businesses found. Try broadening your search.</div> :
        <div className="row g-3">
          {businesses.map(b => (
            <div className="col-md-6" key={b.id}>
              <Link to={`/business/${b.slug}`} className="no-underline" data-testid={`result-${b.slug}`}>
                <div className="biz-card">
                  {b.is_premium ? <div className="badge-premium">Premium</div> : null}
                  <div className="biz-thumb" style={{ backgroundImage: `url(${b.logo_url || b.cover_url || ''})` }} />
                  <div>
                    <div className="fw-bold">{b.name}</div>
                    <div className="small text-muted-2">{b.category_name} · {b.city_name}</div>
                    <div className="small mt-1">{b.tagline}</div>
                    <div className="d-flex gap-2 mt-2">
                      {b.phone && <span className="pill pill-approved">Call</span>}
                      {b.whatsapp && <span className="pill pill-new">WhatsApp</span>}
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}